import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowUp, FiArrowDown, FiUsers } from 'react-icons/fi';

/**
 * Sortable mentee leaderboard. Replaces the flat "Mentee Performance" table
 * from the old dashboard with a denser, sortable view: applications in last
 * 30 days, interview rate, latest resume score, last login, mentor-assigned
 * jobs, and open ticket count. Rows link to the mentee detail page.
 */
const COLUMNS = [
  { key: 'name', label: 'Mentee', numeric: false, align: 'left' },
  { key: 'apps30d', label: 'Apps 30d', numeric: true },
  { key: 'totalApplications', label: 'Total apps', numeric: true },
  { key: 'interviewRate', label: 'Interview rate', numeric: true },
  { key: 'resumeScore', label: 'Resume score', numeric: true },
  { key: 'assignedJobs', label: 'Assigned', numeric: true },
  { key: 'openTickets', label: 'Tickets', numeric: true },
  { key: 'lastLogin', label: 'Last login', numeric: true },
];

const formatLastLogin = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

const ScorePill = ({ score }) => {
  if (score === null || score === undefined) {
    return <span className="text-gray-300">—</span>;
  }
  let cls = 'bg-gray-100 text-gray-700';
  if (score >= 90) cls = 'bg-emerald-100 text-emerald-700';
  else if (score >= 75) cls = 'bg-green-100 text-green-700';
  else if (score >= 60) cls = 'bg-amber-100 text-amber-700';
  else if (score >= 40) cls = 'bg-orange-100 text-orange-700';
  else cls = 'bg-rose-100 text-rose-700';
  return (
    <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${cls}`}>
      {score}
    </span>
  );
};

const TicketBadge = ({ count }) => {
  if (!count) return <span className="text-gray-300">0</span>;
  return (
    <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
      {count}
    </span>
  );
};

const InterviewRate = ({ rate, total }) => {
  if (!total) return <span className="text-gray-300">—</span>;
  const pct = Math.round((rate || 0) * 100);
  let cls = 'text-gray-600';
  if (pct >= 25) cls = 'text-green-700 font-semibold';
  else if (pct >= 10) cls = 'text-gray-800';
  else cls = 'text-orange-700';
  return <span className={cls}>{pct}%</span>;
};

const MenteeLeaderboard = ({ rows = [] }) => {
  const [sortKey, setSortKey] = useState('apps30d');
  const [sortDir, setSortDir] = useState('desc');

  const safeRows = Array.isArray(rows) ? rows : [];

  const sortedRows = useMemo(() => {
    const copy = [...safeRows];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      // Null / undefined always sort to the bottom regardless of direction.
      const aMissing = av === null || av === undefined || av === '';
      const bMissing = bv === null || bv === undefined || bv === '';
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      if (sortKey === 'name') {
        return sortDir === 'asc'
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      }
      if (sortKey === 'lastLogin') {
        const at = new Date(av).getTime();
        const bt = new Date(bv).getTime();
        return sortDir === 'asc' ? at - bt : bt - at;
      }
      const an = Number(av);
      const bn = Number(bv);
      return sortDir === 'asc' ? an - bn : bn - an;
    });
    return copy;
  }, [safeRows, sortKey, sortDir]);

  const onHeaderClick = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  return (
    <div className="bg-white shadow-sm rounded-xl overflow-hidden p-5 border-t-4 border-primary-500 h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Mentee Leaderboard</h3>
          <p className="text-xs text-gray-500">Sort to surface high performers or stragglers</p>
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <FiUsers className="h-3.5 w-3.5" />
          <span>{safeRows.length} mentee{safeRows.length === 1 ? '' : 's'}</span>
        </div>
      </div>

      {safeRows.length === 0 ? (
        <div className="py-8 text-center text-xs text-gray-400">
          No mentees yet — share your invite code below to grow your cohort.
        </div>
      ) : (
        <div className="overflow-x-auto -mx-1 flex-1">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                {COLUMNS.map((col) => {
                  const isActive = col.key === sortKey;
                  return (
                    <th
                      key={col.key}
                      scope="col"
                      className={`px-2 py-2 font-medium select-none cursor-pointer hover:text-gray-700 ${
                        col.align === 'left' ? 'text-left' : 'text-right'
                      }`}
                      onClick={() => onHeaderClick(col.key)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {isActive && (
                          sortDir === 'asc'
                            ? <FiArrowUp className="h-2.5 w-2.5" />
                            : <FiArrowDown className="h-2.5 w-2.5" />
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedRows.map((row) => (
                <tr key={row.menteeId} className="hover:bg-gray-50">
                  <td className="px-2 py-2">
                    <Link
                      to={`/mentor/mentees/${row.menteeId}`}
                      className="block min-w-0"
                    >
                      <div className="font-medium text-gray-900 truncate">{row.name}</div>
                      <div className="text-[10px] text-gray-500 truncate">{row.email}</div>
                    </Link>
                  </td>
                  <td className="px-2 py-2 text-right text-gray-900">{row.apps30d}</td>
                  <td className="px-2 py-2 text-right text-gray-600">{row.totalApplications}</td>
                  <td className="px-2 py-2 text-right">
                    <InterviewRate rate={row.interviewRate} total={row.totalApplications} />
                  </td>
                  <td className="px-2 py-2 text-right">
                    <ScorePill score={row.resumeScore} />
                  </td>
                  <td className="px-2 py-2 text-right text-gray-600">{row.assignedJobs}</td>
                  <td className="px-2 py-2 text-right">
                    <TicketBadge count={row.openTickets} />
                  </td>
                  <td className="px-2 py-2 text-right text-gray-500 text-[11px]">
                    {formatLastLogin(row.lastLogin)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MenteeLeaderboard;
